import React, { useEffect, useContext } from 'react';
import styled from 'styled-components';
import { BookOpen } from 'lucide-react';
import { useDrop } from 'react-dnd';
import { Source, isSource, Note } from '@annote/types';
import { colors, text } from '@annote/core/styles';
import { usePanelContext } from 'src/context/PanelContext';
import { ShownIdsContext } from 'src/context/ShownContext';
import { FocusContext } from 'src/context/FocusContext';
import { useNote, useNotes } from 'src/context/noteCacheHooks';
import { SourceCard } from 'src/components/Cards/byType/source/Source';
import { useDndRef } from 'src/helpers/useDndRef';
import { PANEL_WIDTHS_PX } from 'src/constants/panelSizes';
import { Panel, PanelHeader } from './Panel';
import { PanelToggleButton } from './PanelToggleButton';

const ReaderPanelWrapper = styled(Panel)`
  /* Panel already has display: flex, flex-direction: column */
  /* No overflow needed - content handles its own scrolling */
  z-index: 99; /* Behind library panel (library has z-index: 100) */
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 1rem;
  padding: 2rem;
  color: ${colors.text.placeholder};
  text-align: center;

  svg {
    width: 48px;
    height: 48px;
  }
`;

const EmptyText = styled.div`
  font-family: ${text.family.primary};
  font-size: ${text.size.primary};
`;

const SourceContainer = styled.div`
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const DropOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${colors.bg.dropzone};
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: ${colors.text.primary};

  svg {
    width: 48px;
    height: 48px;
  }
`;

interface ReaderPanelProps {
  isOpen: boolean;
  offset?: number;
}

export const ReaderPanel: React.FC<ReaderPanelProps> = ({ isOpen, offset = 0 }) => {
  const { panels, setActiveId, openPanel, setPanelWidth, getPanelWidth, togglePanel } = usePanelContext();
  const { shownIds } = useContext(ShownIdsContext);
  const { focusedNoteId } = useContext(FocusContext);
  const { activeId } = panels.reader;
  const storedWidth = getPanelWidth('reader');

  // Reactive subscriptions - only re-renders when these specific notes change
  const activeSource = useNote(activeId || undefined);
  const focusedNote = useNote(focusedNoteId || undefined);
  const shownNotes = useNotes(shownIds);

  // Drop zone for sources
  const [{ isOver, canDrop }, dropRef] = useDrop<Note, void, { isOver: boolean; canDrop: boolean }>({
    accept: ['SOURCE'],
    drop: (item: Note) => {
      if (isSource(item)) {
        setActiveId('reader', item.id);
        openPanel('reader', item.id);
      }
    },
    canDrop: (item) => isSource(item),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const setDropRefWrapper = useDndRef(dropRef);

  // Auto-select a source if no active ID is set
  // Priority: focused source > first source in shownIds > null
  useEffect(() => {
    if (activeId || !isOpen) return;

    // Priority 1: If focused note is a source, use it
    if (focusedNote && isSource(focusedNote) && focusedNoteId) {
      setActiveId('reader', focusedNoteId);
      return;
    }

    // Priority 2: First source in shownIds
    const firstSource = shownNotes.find((note) => note && isSource(note));
    if (firstSource) {
      setActiveId('reader', firstSource.id);
    }
  }, [activeId, isOpen, focusedNote, focusedNoteId, shownNotes, setActiveId]);

  // Determine what to render
  const sourceToRender = activeSource && isSource(activeSource) ? (activeSource as Source) : null;
  const showDropOverlay = isOver && canDrop;

  return (
    <ReaderPanelWrapper
      isOpen={isOpen}
      side="left"
      offset={offset}
      resizable={true}
      defaultWidth={storedWidth ?? PANEL_WIDTHS_PX.READER}
      minWidth={400}
      maxWidth={1000}
      onWidthChange={(width) => setPanelWidth('reader', width)}
    >
      <PanelHeader $side="right">
        <PanelToggleButton icon={BookOpen} onClick={() => togglePanel('reader')} title="Close Reader" />
      </PanelHeader>
      <div
        ref={setDropRefWrapper}
        style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}
      >
        {sourceToRender ? (
          <SourceContainer>
            <SourceCard source={sourceToRender} readOnly={true} forceSize="LARGE" inPanel={true} />
            {showDropOverlay && (
              <DropOverlay>
                <BookOpen />
                <div>Drop to read</div>
              </DropOverlay>
            )}
          </SourceContainer>
        ) : (
          <EmptyState>
            <BookOpen />
            <EmptyText>Drop a source here to read</EmptyText>
            {showDropOverlay && (
              <DropOverlay>
                <BookOpen />
                <div>Drop to read</div>
              </DropOverlay>
            )}
          </EmptyState>
        )}
      </div>
    </ReaderPanelWrapper>
  );
};
